import React, { useState, useEffect, useRef } from 'react';
import { SocialCareResult } from '../../types/index';

interface SocialCareAssessmentProps {
  onComplete: (result: SocialCareResult) => void;
  onNext: () => void;
}

interface SocialCareResponses {
  home_environment?: boolean;
  financial_situation?: boolean;
  social_isolation?: boolean;
  social_engagement?: boolean;
}

type SocialCareStep = 
  | 'intro' 
  | 'question1'
  | 'question2' 
  | 'question3'
  | 'question4'
  | 'results'
  | 'carer_correction'
  | 'completed';

const SocialCareAssessment: React.FC<SocialCareAssessmentProps> = ({ onComplete, onNext }) => {
  const [currentStep, setCurrentStep] = useState<SocialCareStep>('intro');
  const [responses, setResponses] = useState<Partial<SocialCareResponses>>({});
  const hasCompletedRef = useRef<boolean>(false);
  const [carerCorrection, setCarerCorrection] = useState({
    homeEnvironmentCorrect: null as boolean | null,
    financialSituationCorrect: null as boolean | null,
    socialIsolationCorrect: null as boolean | null,
    socialEngagementCorrect: null as boolean | null
  });

  const questions = [
    {
      id: 'home_environment',
      text: '您的住房是否有问题？',
      description: '例如房屋状况、位置、安全性等',
      riskIndicator: true // true表示回答"是"代表有风险
    },
    {
      id: 'financial_situation',
      text: '您是否经常资金不足，难以支付食物、住房和医疗费用？',
      description: '请根据实际经济状况诚实回答',
      riskIndicator: true
    },
    {
      id: 'social_isolation',
      text: '您是否经常感到孤独？',
      description: '包括缺乏陪伴或情感支持的感受',
      riskIndicator: true
    },
    {
      id: 'social_engagement',
      text: '您是否难以参与休闲活动和对您重要的其他活动？',
      description: '如社交聚会、兴趣爱好、宗教活动等',
      riskIndicator: true
    }
  ];

  // 评估逻辑实现 - 按WHO ICOPE标准
  const evaluateSocialCare = (testResponses?: Partial<SocialCareResponses>): SocialCareResult => {
    const currentResponses = testResponses || responses;
    const riskAreas = Object.entries(currentResponses)
      .filter(([_, value]) => value === true)
      .map(([key, _]) => key);

    const needsAttention = riskAreas.length > 0;
    
    const socialCareResponses = {
      home_environment: currentResponses.home_environment || false,
      financial_situation: currentResponses.financial_situation || false,
      social_isolation: currentResponses.social_isolation || false,
      social_engagement: currentResponses.social_engagement || false
    };

    return {
      status: needsAttention ? 'NEEDS_ATTENTION' : 'OK',
      riskAreas: riskAreas,
      responses: socialCareResponses,
      recommendations: needsAttention 
        ? ["ICOPE-C11-S11.1-SocialCare-Overview"] 
        : ["ICOPE-C3-S3.1.3-Advice-SocialConnection"]
    };
  };

  const handleResultsConfirm = () => {
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      const result = evaluateSocialCare();
      onComplete(result);
      setCurrentStep('completed');
    }
  };

  const handleCarerCorrection = (
    homeEnvCorrect: boolean,
    financialCorrect: boolean, 
    isolationCorrect: boolean,
    engagementCorrect: boolean
  ) => {
    // 基于照护者修正重新评估社会照护状况
    const correctedResponses = {
      home_environment: !homeEnvCorrect,
      financial_situation: !financialCorrect,
      social_isolation: !isolationCorrect,
      social_engagement: !engagementCorrect
    };
    
    const riskAreas = Object.entries(correctedResponses)
      .filter(([_, value]) => value === true)
      .map(([key, _]) => key);

    const needsAttention = riskAreas.length > 0;
    
    const correctedResult: SocialCareResult = {
      status: needsAttention ? 'NEEDS_ATTENTION' : 'OK',
      riskAreas: riskAreas,
      responses: correctedResponses,
      recommendations: needsAttention 
        ? ["ICOPE-C11-S11.1-SocialCare-Overview"] 
        : ["ICOPE-C3-S3.1.3-Advice-SocialConnection"],
      carerCorrected: true
    };
    
    if (!hasCompletedRef.current) {
      hasCompletedRef.current = true;
      onComplete(correctedResult);
      setCurrentStep('completed');
    }
  };

  const handleQuestionResponse = (questionId: keyof SocialCareResponses, hasRisk: boolean) => {
    const updatedResponses = { ...responses, [questionId]: hasRisk };
    setResponses(updatedResponses);

    // 进入下一个问题或结果
    const questionIndex = questions.findIndex(q => q.id === questionId);
    if (questionIndex < questions.length - 1) {
      const nextStepMap: Record<number, SocialCareStep> = {
        0: 'question2',
        1: 'question3',
        2: 'question4'
      };
      setCurrentStep(nextStepMap[questionIndex]);
    } else {
      setCurrentStep('results');
    }
  };

  useEffect(() => {
    if (currentStep === 'completed') {
      const timer = setTimeout(() => {
        onNext();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  const renderIntro = () => (
    <div className="text-center space-y-4 p-4">
      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900">社会照护与支持评估</h2>
      <p className="text-gray-600">评估您的生活环境和社会支持状况</p>
      
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-left">
        <h3 className="font-semibold text-gray-900 mb-2">关于此评估：</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• 评估住房、经济、社交等关键生活因素</li>
          <li>• 共包含4个简单问题</li>
          <li>• 帮助识别可能需要额外支持的领域</li>
          <li>• 请根据实际情况诚实回答</li>
        </ul>
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
        <p className="text-sm text-yellow-800">
          <strong>隐私保护：</strong> 您的回答仅用于评估分析，不会泄露给任何第三方
        </p>
      </div>
      
      <button 
        onClick={() => setCurrentStep('question1')} 
        className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors"
      >
        开始评估
      </button>
    </div>
  );

  const renderQuestion = (questionIndex: number) => {
    const question = questions[questionIndex];
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-purple-600">{questionIndex + 1}</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">社会照护评估</h2>
          <p className="text-gray-600">问题 {questionIndex + 1} / 4</p>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{question.text}</h3>
          <p className="text-sm text-gray-600 mb-6">{question.description}</p>
          
          <div className="space-y-3">
            <button
              onClick={() => handleQuestionResponse(
                question.id as keyof SocialCareResponses, 
                true
              )}
              className="w-full p-4 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-red-800">是的，我有这方面的问题</span>
                <span className="text-sm text-red-600">存在风险</span>
              </div>
            </button>
            
            <button
              onClick={() => handleQuestionResponse(
                question.id as keyof SocialCareResponses, 
                false
              )}
              className="w-full p-4 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-green-800">没有，这方面没有问题</span>
                <span className="text-sm text-green-600">状况良好</span>
              </div>
            </button>
          </div>
        </div>
        
        <div className="text-center">
          <div className="flex justify-center space-x-2">
            {[0, 1, 2, 3].map((i) => (
              <div 
                key={i} 
                className={`w-3 h-3 rounded-full ${
                  i === questionIndex ? 'bg-purple-600' : 'bg-gray-300'
                }`} 
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const result = evaluateSocialCare();
    const riskCount = result.riskAreas.length;
    const needsAttention = result.status === 'NEEDS_ATTENTION';
    
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'OK': return 'text-green-600';
        case 'NEEDS_ATTENTION': return 'text-orange-600';
        default: return 'text-gray-600';
      }
    };

    const getStatusBg = (status: string) => {
      switch (status) {
        case 'OK': return 'bg-green-50 border-green-200';
        case 'NEEDS_ATTENTION': return 'bg-orange-50 border-orange-200';
        default: return 'bg-gray-50 border-gray-200';
      }
    };
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            needsAttention ? 'bg-orange-100' : 'bg-green-100'
          }`}>
            <svg className={`w-8 h-8 ${getStatusColor(result.status)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">评估完成</h2>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">社会照护评估结果：</h3>
          
          <div className={`p-4 rounded-lg border ${getStatusBg(result.status)} mb-4`}>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getStatusColor(result.status)} mb-1`}>
                {needsAttention ? '需要关注' : '状况良好'}
              </div>
              <p className="text-gray-600">社会照护状态</p>
              <p className="text-sm text-gray-500 mt-1">{riskCount}/4 项需要关注</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className={`text-center p-3 rounded border ${
              result.responses.home_environment ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}>
              <div className={`font-bold ${result.responses.home_environment ? 'text-red-600' : 'text-green-600'} mb-1`}>
                {result.responses.home_environment ? '有问题' : '良好'}
              </div>
              <p className="text-xs text-gray-600">住房环境</p>
            </div>
            
            <div className={`text-center p-3 rounded border ${
              result.responses.financial_situation ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}>
              <div className={`font-bold ${result.responses.financial_situation ? 'text-red-600' : 'text-green-600'} mb-1`}>
                {result.responses.financial_situation ? '有困难' : '良好'}
              </div>
              <p className="text-xs text-gray-600">经济状况</p>
            </div>
            
            <div className={`text-center p-3 rounded border ${
              result.responses.social_isolation ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}>
              <div className={`font-bold ${result.responses.social_isolation ? 'text-red-600' : 'text-green-600'} mb-1`}>
                {result.responses.social_isolation ? '常孤独' : '良好'}
              </div>
              <p className="text-xs text-gray-600">社交孤立</p>
            </div>
            
            <div className={`text-center p-3 rounded border ${
              result.responses.social_engagement ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}>
              <div className={`font-bold ${result.responses.social_engagement ? 'text-red-600' : 'text-green-600'} mb-1`}>
                {result.responses.social_engagement ? '有困难' : '良好'}
              </div>
              <p className="text-xs text-gray-600">社会参与</p>
            </div>
          </div>

          <div className={`p-3 rounded ${getStatusBg(result.status)}`}>
            <h4 className={`font-semibold ${getStatusColor(result.status)} mb-2`}>评估说明：</h4>
            <p className={`text-sm ${getStatusColor(result.status).replace('text-', 'text-').replace('-600', '-700')}`}>
              {needsAttention 
                ? '检测到社会照护方面需要关注的问题，建议寻求相关支持服务'
                : '您的社会照护状况良好，请继续保持积极的社会参与'}
            </p>
          </div>
          
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={handleResultsConfirm}
              className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              确认结果，完成测试
            </button>
            <button
              onClick={() => setCurrentStep('carer_correction')}
              className="w-full bg-orange-500 text-white py-3 px-6 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>照护者修正答案</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCarerCorrection = () => {
    const result = evaluateSocialCare();
    const needsAttention = result.status === 'NEEDS_ATTENTION';
    
    return (
      <div className="p-4 space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">照护者答案确认</h2>
          <p className="text-gray-600">请照护者根据实际情况确认答案正确性</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">系统评估结果回顾</h3>
            
            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`text-center p-3 rounded border ${
                  result.responses.home_environment ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div className={`font-bold ${result.responses.home_environment ? 'text-red-600' : 'text-green-600'} mb-1`}>
                    {result.responses.home_environment ? '有问题 ❌' : '良好 ✅'}
                  </div>
                  <p className="text-xs text-gray-600">住房环境</p>
                </div>
                
                <div className={`text-center p-3 rounded border ${
                  result.responses.financial_situation ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div className={`font-bold ${result.responses.financial_situation ? 'text-red-600' : 'text-green-600'} mb-1`}>
                    {result.responses.financial_situation ? '有困难 ❌' : '良好 ✅'}
                  </div>
                  <p className="text-xs text-gray-600">经济状况</p>
                </div>
                
                <div className={`text-center p-3 rounded border ${
                  result.responses.social_isolation ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div className={`font-bold ${result.responses.social_isolation ? 'text-red-600' : 'text-green-600'} mb-1`}>
                    {result.responses.social_isolation ? '常孤独 ❌' : '良好 ✅'}
                  </div>
                  <p className="text-xs text-gray-600">社交孤立</p>
                </div>
                
                <div className={`text-center p-3 rounded border ${
                  result.responses.social_engagement ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                }`}>
                  <div className={`font-bold ${result.responses.social_engagement ? 'text-red-600' : 'text-green-600'} mb-1`}>
                    {result.responses.social_engagement ? '有困难 ❌' : '良好 ✅'}
                  </div>
                  <p className="text-xs text-gray-600">社会参与</p>
                </div>
              </div>
              
              <div className={`p-3 rounded-lg text-center ${needsAttention ? 'bg-orange-50 border border-orange-200' : 'bg-green-50 border border-green-200'}`}>
                <span className={`font-medium ${needsAttention ? 'text-orange-800' : 'text-green-800'}`}>
                  系统评估：{needsAttention ? '❌ 需要关注' : '✅ 状况良好'}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">照护者确认</h3>
            <p className="text-gray-700 mb-4">
              请照护者根据对老年人实际情况的了解，确认以下各项的状况：
            </p>
            
            <div className="space-y-4">
              {/* 住房环境确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">住房环境状况确认</h4>
                <p className="text-sm text-gray-600 mb-3">住房是否存在安全、位置、条件等方面的问题？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, homeEnvironmentCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.homeEnvironmentCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.homeEnvironmentCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.homeEnvironmentCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 住房环境良好，无明显问题</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, homeEnvironmentCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.homeEnvironmentCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.homeEnvironmentCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.homeEnvironmentCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 住房环境存在问题</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 经济状况确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">经济状况确认</h4>
                <p className="text-sm text-gray-600 mb-3">是否经常资金不足，难以支付基本生活费用？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, financialSituationCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.financialSituationCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.financialSituationCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.financialSituationCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 经济状况良好，能满足基本需求</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, financialSituationCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.financialSituationCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.financialSituationCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.financialSituationCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 经济状况困难</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 社交孤立确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">社交孤立状况确认</h4>
                <p className="text-sm text-gray-600 mb-3">是否经常感到孤独，缺乏陪伴或情感支持？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, socialIsolationCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.socialIsolationCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.socialIsolationCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.socialIsolationCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 社交正常，不感到孤独</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, socialIsolationCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.socialIsolationCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.socialIsolationCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.socialIsolationCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 经常感到孤独</span>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* 社会参与确认 */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">社会参与状况确认</h4>
                <p className="text-sm text-gray-600 mb-3">是否难以参与休闲活动和重要的社会活动？</p>
                <div className="space-y-2">
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, socialEngagementCorrect: true }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.socialEngagementCorrect === true
                        ? 'bg-green-50 border-green-300 text-green-800'
                        : 'bg-gray-50 border-gray-200 hover:border-green-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.socialEngagementCorrect === true ? 'bg-green-100 border-green-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.socialEngagementCorrect === true && (
                          <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">✅ 能正常参与社会活动</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setCarerCorrection(prev => ({ ...prev, socialEngagementCorrect: false }))}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      carerCorrection.socialEngagementCorrect === false
                        ? 'bg-red-50 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 hover:border-red-300 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        carerCorrection.socialEngagementCorrect === false ? 'bg-red-100 border-red-400' : 'border-gray-300'
                      }`}>
                        {carerCorrection.socialEngagementCorrect === false && (
                          <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium">❌ 难以参与社会活动</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => handleCarerCorrection(
              carerCorrection.homeEnvironmentCorrect!,
              carerCorrection.financialSituationCorrect!,
              carerCorrection.socialIsolationCorrect!,
              carerCorrection.socialEngagementCorrect!
            )}
            disabled={
              carerCorrection.homeEnvironmentCorrect === null ||
              carerCorrection.financialSituationCorrect === null ||
              carerCorrection.socialIsolationCorrect === null ||
              carerCorrection.socialEngagementCorrect === null
            }
            className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 disabled:bg-gray-300 transition-colors font-medium"
          >
            确认修正，完成评估
          </button>
        </div>
      </div>
    );
  };

  const renderCompleted = () => (
    <div className="text-center p-4">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">社会照护评估完成</h2>
      <p className="text-gray-600">正在转到下一项...</p>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'intro': return renderIntro();
      case 'question1': return renderQuestion(0);
      case 'question2': return renderQuestion(1);
      case 'question3': return renderQuestion(2);
      case 'question4': return renderQuestion(3);
      case 'results': return renderResults();
      case 'carer_correction': return renderCarerCorrection();
      case 'completed': return renderCompleted();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {renderStep()}
      </div>
    </div>
  );
};

export default SocialCareAssessment;